import { ReactNode } from "react";
import {
    ArticleSearchResultResponse,
    Collection,
    UserResponse,
    CollectionRuleResponse,
    CollectionRuleType,
    UserProfileStyle,
    PublicUserResponse,
    ProfileUserResponse,
    PublicCollectionResponse
} from "@authory-packages/core";
import { InfinitePaginationFooter } from "../../components/InfinitePaginationFooter";
import { OGTags, SEO_TYPE } from "../../components/OGTags";
import { ContactUserFormik } from "../../components/V3ContactUser";
import { V3ProfileEmptyCollection } from "../../components/V3EmptyStateScreen";
import { V3ProfileList, V3ProfileListProps } from "../../components/V3ProfileList";
import { VerticalSpacing } from "../../components/VerticalSpacing";
import { CollectionMetaSizer, CollectionPublicSpacer } from "./CollectionDetailsPublicPageLayout.styles";
import { CollectionHeader } from "../../components/CollectionHeader";
import { CreateCollectionHandler } from "../../components/CreateUpdateCollectionModal";
import { SourceInProgress } from "../../components/SourceInProgress";
import { useInView } from "react-intersection-observer";
import { V3PublicAuthorHeader } from "../../components/V3PublicAuthorHeader";
import { InlineLink } from "../../components/ReusableStyledComponents/InlineLink";
import { CollectionBannerDetails } from "../../components/CollectionBannerDetails";
import { showCollectionAutoUpdateBanner } from "../../utils/collectionBanners";
import { BannerDetails } from "../../components/BannerDetails";
import { ContentGridWithFooter } from "../../components/ContentGridWithFooter";
import { V4ProfileHero } from "../../components/ProfileHero";
import { MediaEditingFormik } from "../../components/V3MediaEditingModal";
import { ItemUrlContext } from "../../components/V3ProfileList/V3ProfileList.utils";

export interface V3CollectionPublicPageLayoutProps {
    author: UserResponse | PublicUserResponse | ProfileUserResponse;
    items: ArticleSearchResultResponse[];
    collection: Collection | PublicCollectionResponse;
    itemsLoading: boolean;
    smartShareUrl?: string;
    isPrivateView?: boolean;
    contentCanFetchmore: boolean;
    contentLoadmore: () => void;
    onContactUserSubmit: (data: ContactUserFormik) => Promise<void>;
    onSubscribeFormSubmit: (email: string) => Promise<void>;
    onCollectionUpdateInfo?: CreateCollectionHandler;
    onCoverModalsubmit?: (data: MediaEditingFormik) => Promise<void>;
    onEditCoverClick?: () => void;
    onRulesClickCallback?: () => void;
    onItemClick?: (id: string) => void;
    rules: CollectionRuleResponse[];
    isStandaloneCollectionView?: boolean;
    renderProfileList?: (props: V3ProfileListProps) => ReactNode;
}

export const V3CollectionPublicPageLayout = ({
    author,
    items,
    collection,
    itemsLoading,
    isPrivateView = false,
    contentLoadmore,
    contentCanFetchmore,
    smartShareUrl,
    onContactUserSubmit,
    onSubscribeFormSubmit,
    onCollectionUpdateInfo,
    onCoverModalsubmit,
    onRulesClickCallback,
    onEditCoverClick,
    onItemClick,
    rules,
    isStandaloneCollectionView = true,
    renderProfileList = (props) => <V3ProfileList {...props} />
}: V3CollectionPublicPageLayoutProps) => {
    const hasItems = !!items.reduce((acc, curr) => acc + curr.articles.length, 0) || (!!items.length && !!items[0].pinned);
    const isEmptyCollection = !itemsLoading && !hasItems;

    const srcInProgress = (
        "sources" in author && "default" in collection
            ? collection?.default
                ? author.sources
                : author.sources.filter((src) =>
                      (rules.find((rule) => rule.type === CollectionRuleType.Source)?.params || []).find((it) => it === src?.canonicalSlug)
                  )
            : []
    ).filter((src) => src.inProgress);

    const { ref, inView } = useInView({
        threshold: 0,
        rootMargin: "0px 0px 0px",
        initialInView: true
    });

    const useSidebarLayout = "profileStyle" in author && !isStandaloneCollectionView && author.profileStyle === UserProfileStyle.sidebar;

    return (
        <>
            {author && !isPrivateView && (
                <OGTags
                    user={author}
                    target={SEO_TYPE.COLLECTION}
                    collection={collection}
                    context={isStandaloneCollectionView ? ItemUrlContext.collection : ItemUrlContext.profile}
                    forceNoIndex={!!smartShareUrl || isEmptyCollection}
                />
            )}
            {!isPrivateView && !inView && (
                <V3PublicAuthorHeader user={author} onContactUserSubmit={onContactUserSubmit} showAboutPagesNav={!isStandaloneCollectionView} />
            )}
            <ContentGridWithFooter
                isPrivateView={isPrivateView}
                showAuthoryBranding={author.profileAuthoryBranding}
                skipSideBarDesktop={!useSidebarLayout}
                dynamicContent={
                    !useSidebarLayout ? (
                        <div ref={ref}>
                            <CollectionHeader
                                collection={collection}
                                editMode={isPrivateView}
                                onCollectionUpdateInfo={onCollectionUpdateInfo}
                                onEditCoverClick={onEditCoverClick}
                                user={author}
                                onContactUserSubmit={onContactUserSubmit}
                                isStandaloneCollectionView={isStandaloneCollectionView}
                                onCoverModalsubmit={onCoverModalsubmit}
                                smartShareUrl={smartShareUrl}
                            />
                        </div>
                    ) : (
                        <>
                            {"bio" in author && (
                                <V4ProfileHero
                                    isProfileCollectionView
                                    author={author}
                                    onContactUserSubmit={onContactUserSubmit}
                                    editMode={isPrivateView}
                                    onSubscribeFormSubmit={onSubscribeFormSubmit}
                                    isAboutPage={false}
                                    onAboutPageSelect={() => {}}
                                    smartShareUrl={smartShareUrl}
                                />
                            )}
                        </>
                    )
                }
                mainContent={
                    <CollectionPublicSpacer>
                        <div>
                            {useSidebarLayout && (
                                <CollectionHeader
                                    useBorderRadius
                                    collection={collection}
                                    editMode={isPrivateView}
                                    onCollectionUpdateInfo={onCollectionUpdateInfo}
                                    onEditCoverClick={onEditCoverClick}
                                    user={author}
                                    onContactUserSubmit={onContactUserSubmit}
                                    isStandaloneCollectionView={isStandaloneCollectionView}
                                />
                            )}
                        </div>
                        <div>
                            <CollectionMetaSizer $viewType={collection.view} $isStandaloneCollectionView={isStandaloneCollectionView}>
                                {isPrivateView && "ruleCount" in collection && (
                                    <>
                                        {showCollectionAutoUpdateBanner(collection) && (
                                            <CollectionBannerDetails
                                                hasFiltersEnabled={!!collection?.ruleCount}
                                                onCollectionUpdateRules={() => onRulesClickCallback && onRulesClickCallback()}
                                            />
                                        )}
                                        {!!srcInProgress.length && (
                                            <VerticalSpacing top={12} bottom={12}>
                                                <SourceInProgress sources={srcInProgress} allowUserThemable />
                                            </VerticalSpacing>
                                        )}
                                        {collection.partOfProfile && (
                                            <VerticalSpacing top={12}>
                                                <BannerDetails useYellow allowUserThemable>
                                                    This collection is displayed on your portfolio, but you can also use it standalone.{" "}
                                                    <InlineLink
                                                        target="_blank"
                                                        href="https://authory.com/help/en/articles/9015142-collections-on-your-portfolio-vs-standalone-collections"
                                                    >
                                                        Learn more
                                                    </InlineLink>{" "}
                                                </BannerDetails>
                                            </VerticalSpacing>
                                        )}
                                    </>
                                )}
                                <VerticalSpacing top={48}>
                                    {hasItems &&
                                        renderProfileList({
                                            items,
                                            context: isStandaloneCollectionView ? ItemUrlContext.collection : ItemUrlContext.profile,
                                            authorSlug: author.slug,
                                            viewType: collection.view,
                                            itemsLoading,
                                            pinnedItem: !!items.length ? items[0].pinned : undefined,
                                            collection,
                                            isPrivateView,
                                            smartShareUrl,
                                            onItemClick
                                        })}
                                    {!itemsLoading && !hasItems && (
                                        <V3ProfileEmptyCollection isCollection isPrivate={isPrivateView} isDefaultCollection={collection.default} />
                                    )}
                                    <InfinitePaginationFooter
                                        contentLoading={itemsLoading}
                                        numPagesLoaded={items.length}
                                        contentCanFetchmore={contentCanFetchmore}
                                        loadMore={contentLoadmore}
                                    />
                                </VerticalSpacing>
                            </CollectionMetaSizer>
                        </div>
                    </CollectionPublicSpacer>
                }
            />
        </>
    );
};
